// The per-device payout-flow picker, shared by both places a device's clicker is set up:
// the reconfigure modal (device card) and the launch panel (the farm's Clicker page).
//
// One implementation because the two forms must agree on more than markup — which flows may
// be offered (this farm's team plus the global ones, exactly what the launch resolves), when
// a stored pick is dropped, and what the form sends. Two copies of that drifted apart the
// moment either side was touched, and the failure mode is silent: a device pointed at a flow
// the launch then ignores, or a save that quietly writes null over the pick.
import { esc } from '../ui/modal';
import { t } from '../ui/lang';
import { fetchFarmFlows } from '../flows/flows-shared';
/** The field's markup. Hidden until the flows are in — see wireFlowField. */
export function flowFieldHtml() {
    return `
        <div class="modal__field dc-flow-field" style="display:none">
            <label>${t('clicker.payout_flow')}</label>
            <select class="settings-input dc-flow"></select>
        </div>`;
}
/**
 * How a pick is filed in the device's `flow_picks` — mirrors the server's flowPickKey
 * (server/farmClicker.ts), which stores it and restores it the same way on a save that
 * moves the pair without naming a flow.
 */
const pickKey = (taskType, app) => `${taskType}:${app}`;
/**
 * Wire the field inside `root`, which must already contain `flowFieldHtml()`.
 *
 * `typeSel`/`appSel` are the form's own task-type and app selects: a flow belongs to one
 * (task type, app) pair, so the list narrows with them.
 */
export function wireFlowField(root, opts) {
    const field = root.querySelector('.dc-flow-field');
    const sel = root.querySelector('.dc-flow');
    // No role gate of its own: pointing a device at a flow is part of configuring that
    // device's clicker, so whoever may open this form may do it. Authoring flows is the
    // separate, higher privilege — which is why the list is names, not documents.
    let all = [];
    // The list and the app arrive from two independent fetches. Until BOTH are in, "the
    // pick is not in the list" says nothing — it is just as likely that the list is empty
    // or the app is still unset — and pruning on that guess silently dropped the device's
    // stored flow whenever the flows fetch won the race, so Save wrote a null back.
    let loaded = false;
    // What the form will save: the stored pick until the operator changes it. Held apart
    // from the <select>, which is rebuilt whenever the task type or app narrows the list.
    let picked = opts.initialBuiltIn ? null : opts.initial;
    // The form no longer offers the clicker's own code. A device still stored on it keeps
    // that only while the flow list failed to load — a network blip must not repoint it.
    let builtIn = opts.initialBuiltIn ?? false;
    // The device's picks for the OTHER (task type, app) pairs, so switching either restores
    // what was chosen for it instead of dropping it.
    const picks = { ...(opts.picks ?? {}) };
    // The pair the current pick belongs to; '' until the app is known.
    let pairKey = '';
    const repopulate = () => {
        const key = opts.appSel.value ? pickKey(opts.typeSel.value, opts.appSel.value) : '';
        if (key && key !== pairKey) {
            // File what is on screen under the pair it was chosen for, then restore this
            // pair's own. Skipped on the FIRST settle: the stored pick already belongs to it.
            if (pairKey) {
                picks[pairKey] = { payout_flow_id: picked, built_in_flow: false };
                picked = picks[key]?.payout_flow_id ?? null;
            }
            pairKey = key;
        }
        if (!loaded)
            return;
        field.style.display = '';
        builtIn = false;
        const matching = all.filter((f) => f.enabled && f.task_type === opts.typeSel.value && f.app === opts.appSel.value);
        // No pick, or one written for another app / not usable by this farm's team: fall to
        // the pair's default flow, else its first. Before the render, so what is on screen
        // and what will be saved cannot disagree.
        if (opts.appSel.value && !matching.some((f) => f.id === picked)) {
            picked = (matching.find((f) => f.default_pick) ?? matching[0])?.id ?? null;
        }
        sel.disabled = !matching.length;
        sel.innerHTML = matching.length
            ? matching.map((f) => `<option value="${f.id}"${picked === f.id ? ' selected' : ''}>${esc(f.name)}</option>`).join('')
            : `<option value="" selected>${esc(t('clicker.payout_flow_none'))}</option>`;
    };
    sel.addEventListener('change', () => {
        picked = sel.value ? Number(sel.value) : null;
    });
    void fetchFarmFlows(opts.farmId)
        .then((f) => { all = f; loaded = true; repopulate(); })
        // A list that failed to load must not look like an empty one: leave the pick alone
        // rather than saving a null over it because of a network blip.
        .catch(() => { all = []; });
    return {
        picked: () => ({ payout_flow_id: builtIn ? null : picked, built_in_flow: builtIn }),
        repopulate,
    };
}
